import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ButtonGroup, Button, Row, Col } from 'reactstrap';
import ReactTooltip from "react-tooltip";
import { Utils } from '../../../../services';
import { Loader, Tooltip } from '../../../../components';
import { loadTasks, getTasksLinkedResources } from '../../../../redux';
import moment from 'moment';
import _ from 'lodash';

import './UserPlanning.css';

const UserPlanning = () => {
    const { t, i18n } = useTranslation();
    const dispatch    = useDispatch();

    const me = useSelector(state => state.auth.user);

    const { tasks, isLoading, loadingErrorMessage, linkedResources } = useSelector(state => state.tasks);

    const { activities } = linkedResources || {};

    useEffect(() => {
        dispatch(getTasksLinkedResources());
    }, [dispatch])

    useEffect(() => {
        dispatch(loadTasks({
            user_id: me.id,
            start_at_gte: moment(),
        }));
    }, [dispatch, me])

    const myTasks = React.useMemo(() => {
        if (!me || !tasks?.length)
            return [];

        return _.sortBy(
            tasks.filter(t => t.user_id === me.id),
            'start_at'
        );
    }, [tasks, me]);

    const content = React.useMemo(() => {
        moment.locale(i18n.language);
        const sectors =  _.uniqBy(myTasks.map(t => t.shift?.sector).filter(s => !!s), 'id');
        const screenings =  _.uniqBy(myTasks.map(t => t.screening).filter(s => !!s), 'id');
        const admins =  _.uniqBy(myTasks.map(t => t.shift?.sector?.admins?.map(a => a._id)).filter(s => !!s), '_id');

        return (
            <Row className="flex-1">
                <Col>
                    <div className='h-10 border-bottom'>
                        <h5>{ t('dashboard.userPlanning.title') }</h5>
                    </div>

                    {isLoading && (
                        <Loader />
                    )}

                    {!isLoading && loadingErrorMessage && (
                        <h6 className="mt-2 text-italic text-danger">
                            { loadingErrorMessage }
                        </h6>
                    )}

                    {!isLoading && !loadingErrorMessage && myTasks?.length > 0 && (
                    <>
                        <table className="table table-bordered table-striped table-hover mt-3">
                            <thead>
                                <tr>
                                    <th className="text-center">{ t('dashboard.userPlanning.timing') }</th>
                                    {activities?.length > 1 && (
                                        <th className="text-center">{ t('dashboard.userPlanning.activity') }</th>
                                    )}
                                    {sectors?.length > 0 && (
                                        <th className="text-center">{ t('dashboard.userPlanning.sector') }</th>
                                    )}
                                    {screenings?.length > 0 && (
                                        <th className="text-center">{ t('dashboard.userPlanning.screening') }</th>
                                    )}
                                    {admins?.length > 0 && (
                                        <th className="text-center">{ t('dashboard.userPlanning.admins') }</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {myTasks?.map((task, index) => (
                                <tr key={ task.id }>
                                    <td>
                                        <div className="d-flex flex-column">
                                            <div>
                                                <b>{moment(task.start_at).format('ll')}</b>&nbsp;
                                            </div>
                                            <div className="text-right">
                                                {moment(task.start_at).format('LT')}&nbsp;&#x279F;&nbsp;
                                                {moment(task.stop_at).format('LT')}
                                            </div>
                                        </div>
                                    </td>
                                    {(activities?.length > 1) && (
                                        <td align="center">
                                            {task.activity && Utils.localized_or_fallback(task.activity.name, i18n.language)}
                                        </td>
                                    )}
                                    {(sectors.length > 0) && (
                                        <td align="center">
                                            <div className="text-bold">
                                                <span>
                                                    { task.shift?.sector && Utils.localized_or_fallback(task.shift.sector.name, i18n.language) }
                                                </span>
                                                {task.shift.sector.description[i18n.language]?.length > 0 && (
                                                <>
                                                    &nbsp;
                                                    <Tooltip id={`sector_task_${index}`} label={ Utils.localized_or_fallback(task.shift.sector.description, i18n.language)} />
                                                </>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    {(screenings.length > 0) && (
                                        <td align="center">
                                            { task.screening && Utils.localized_or_fallback(task.screening.title, i18n.language) }
                                        </td>
                                    )}
                                    {(admins.length > 0) && (
                                        <td align="center">
                                            { task.shift.sector.admins?.map(admin => (
                                                <div className="badge badge-light ml-2">
                                                    { admin.mobile_phone?.length ?
                                                        <a href={Utils.getPhoneNumberUri(admin.mobile_phone)}>
                                                            {admin.fullname}
                                                            <div className="small">
                                                                {Utils.formatPhoneNumber(admin.mobile_phone)}
                                                            </div>
                                                        </a> :
                                                        admin.fullname
                                                    }
                                                </div>
                                            ))}
                                        </td>
                                    )}
                                </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="text-center">
                            <ButtonGroup>
                                {me && (
                                    <Button tag={Link} to={`/users/edit/${me?.id}#team`} className="btn btn-warning">
                                        <i className="fa fa-pencil" />&nbsp;
                                        { t('widgets.change_availabilities') }
                                    </Button>
                                )}
                                <Button tag={Link} to="/tasks" className="btn btn-info">
                                    <i className="fa fa-calendar" />&nbsp;
                                    { t('widgets.go_to_planning') }
                                </Button>
                            </ButtonGroup>
                        </div>
                    </>
                    )}

                    {!isLoading && !loadingErrorMessage && tasks?.length === 0 && (
                        <h6 className="mt-2 text-italic">
                            { t('widgets.no_planning_entries') }
                        </h6>
                    )}
                </Col>
            </Row>
        );
    }, [myTasks, i18n.language]);

    return (
        <div className='UserPlanning'>
            { content }
        </div>
    )
}

export default UserPlanning;
